/**
 * AchievementsSettings.tsx
 *
 * Standard React achievements settings page using HTML/CSS
 * Displays player achievements and statistics
 */
import React from 'react';
import { useTheme } from '../../services/ThemeContext';
import { useAchievements, ACHIEVEMENT_DEFINITIONS } from '../../services/AchievementsContext';
import { ScrollableModal } from './Modal';

interface AchievementsSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatTime = (ms: number): string => {
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

const AchievementsSettings: React.FC<AchievementsSettingsProps> = ({ isOpen, onClose }) => {
  const { currentTheme: colors } = useTheme();
  const { stats, achievements, resetProgress, checkDependencies } = useAchievements();

  if (!isOpen) return null;

  // Get progress for an achievement
  const getProgress = (achievementId: string) => {
    const achievement = ACHIEVEMENT_DEFINITIONS.find(a => a.id === achievementId);
    if (!achievement) return { current: 0, max: 100, percent: 0, label: '' };

    const { type, value } = achievement.requirement;
    let current = 0;
    let label = '';

    switch (type) {
      case 'turns':
        current = stats.totalTurns;
        label = `${stats.totalTurns} / ${value} turns`;
        break;
      case 'playtime':
        current = stats.totalPlaytimeMs;
        label = `${formatTime(stats.totalPlaytimeMs)} / ${formatTime(value)}`;
        break;
      case 'verses':
        current = stats.uniqueVerses;
        label = `${stats.uniqueVerses} / ${value} verses`;
        break;
      case 'single_session':
        current = stats.currentSessionMs;
        label = `${formatTime(stats.currentSessionMs)} / ${formatTime(value)}`;
        break;
      case 'words':
        current = stats.totalWords;
        label = `${stats.totalWords.toLocaleString()} / ${value.toLocaleString()} words`;
        break;
      default:
        return { current: 0, max: value, percent: 0, label: 'Unknown' };
    }

    const percent = Math.min((current / value) * 100, 100);
    return { current, max: value, percent, label };
  };

  // Check if achievement is unlocked
  const isUnlocked = (achievementId: string): boolean => {
    return achievements.some(a => a.id === achievementId);
  };

  // Get emoji for achievement type
  const getEmoji = (type: string, value: number): string => {
    switch (type) {
      case 'turns':
        if (value >= 500) return '';
        if (value >= 100) return '';
        return '';
      case 'playtime':
        if (value >= 2 * 60 * 60 * 1000) return '';
        return '';
      case 'verses':
        if (value >= 20) return '';
        return '';
      case 'single_session':
        return '';
      case 'words':
        return '';
      default:
        return '';
    }
  };

  // Count unlocked achievements
  const unlockedCount = achievements.length;
  const totalAchievements = ACHIEVEMENT_DEFINITIONS.length;
  const progressPercent = Math.round((unlockedCount / totalAchievements) * 100);

  return (
    <ScrollableModal isOpen={isOpen} onClose={onClose} title="Achievements">
      {/* Stats Summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        padding: 16,
        marginBottom: 24,
        backgroundColor: `${colors.primary}11`,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.primary,
        borderStyle: 'solid',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: colors.primary }}>{stats.totalTurns}</div>
          <div style={{ fontSize: 12, color: colors.textMuted }}>Turns</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: colors.primary }}>{formatTime(stats.totalPlaytimeMs)}</div>
          <div style={{ fontSize: 12, color: colors.textMuted }}>Playtime</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: colors.primary }}>{stats.uniqueVerses}</div>
          <div style={{ fontSize: 12, color: colors.textMuted }}>Verses</div>
        </div>
      </div>

      {/* Progress Overview */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 14, fontWeight: '600', color: colors.textMain }}>Progress</span>
          <span style={{ fontSize: 14, color: colors.primary }}>{unlockedCount} / {totalAchievements} unlocked</span>
        </div>
        <div style={{ height: 8, backgroundColor: colors.background, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${progressPercent}%`,
            backgroundColor: colors.primary,
            borderRadius: 4,
          }} />
        </div>
      </div>

      {/* Achievements List */}
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {ACHIEVEMENT_DEFINITIONS.map(achievement => {
          const unlocked = isUnlocked(achievement.id);
          const progress = getProgress(achievement.id);
          const lockedByPrereqs = achievement.prerequisites && !achievement.prerequisites.some(p => isUnlocked(p));

          return (
            <div
              key={achievement.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                marginBottom: 8,
                backgroundColor: unlocked ? `${colors.primary}15` : 'transparent',
                borderRadius: 8,
                borderWidth: 1,
                borderColor: unlocked ? colors.primary : colors.border,
                borderStyle: 'solid',
                opacity: lockedByPrereqs ? 0.5 : 1,
              }}
            >
              {/* Icon */}
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: unlocked ? colors.primary : colors.background,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
              }}>
                {unlocked ? getEmoji(achievement.requirement.type, achievement.requirement.value) : ''}
              </div>

              {/* Info */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 'bold', color: colors.textMain, marginBottom: 2 }}>
                  {achievement.name}
                  {lockedByPrereqs && <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 8 }}>(Locked)</span>}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>
                  {achievement.description}
                </div>
                <div style={{ fontSize: 11, color: colors.primary }}>
                  {progress.label}
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{ width: 60 }}>
                <div style={{ height: 4, backgroundColor: colors.background, borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${progress.percent}%`,
                    backgroundColor: unlocked ? colors.primary : colors.textMuted,
                    borderRadius: 2,
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reset Button */}
      {unlockedCount > 0 && (
        <button
          onClick={() => {
            if (confirm('Are you sure you want to reset all achievements and statistics? This cannot be undone.')) {
              resetProgress();
            }
          }}
          style={{
            width: '100%',
            padding: 12,
            marginTop: 24,
            borderRadius: 8,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.border,
            borderStyle: 'solid',
            color: colors.textMuted,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Reset All Progress
        </button>
      )}
    </ScrollableModal>
  );
};

export { AchievementsSettings };
